import { useEffect, useState, useMemo } from "react";
import { Plus, Mail, CheckCircle, Trash2, Columns3, Pencil, Printer, FileDown, Copy, FileMinus2, Undo2, FileText, Download } from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Badge } from "../components/ui/Badge";
import { Modal } from "../components/ui/Modal";
import { AlertModal } from "../components/ui/AlertModal";
import { PageHeader } from "../components/ui/PageHeader";
import { Select } from "../components/ui/Select";
import { CurrencySelect } from "../components/ui/CurrencySelect";
import { EditableChip } from "../components/ui/EditableChip";
import { DataTable, type Column } from "../components/ui/DataTable";
import { TableExportToolbar } from "../components/TableExportToolbar";
import { PrintTable } from "../components/PrintTable";
import { RowActions } from "../components/ui/RowActions";
import { eur, fmtCurrency, fmtDate, fmtTime, nlStatus } from "../lib/format";
import { exportRowsToCSV } from "../lib/exportCsv";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { ContactFormModal } from "../components/contacts/ContactFormModal";
import { PreviewModal } from "../components/PreviewModal";
import { EmailModal } from "../components/email/EmailModal";

interface Contact {
  id: string;
  name: string;
}

interface VatRate {
  id: string;
  percentage: number;
  name: string;
}

interface CustomColumn {
  id: string;
  name: string;
}

interface Line {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRateId: string;
  customValues: Record<string, string>;
}

export default function Invoices() {
  const { user, activeCompany } = useAuth();
  const isSuperadmin = user?.role === "SUPERADMIN";
  const [invoices, setInvoices] = useState<any[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [vatRates, setVatRates] = useState<VatRate[]>([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<any | null>(null);

  const [contactId, setContactId] = useState("");
  const [showContactModal, setShowContactModal] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [dueDate, setDueDate] = useState("");
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  const [lines, setLines] = useState<Line[]>([
    { description: "", quantity: 1, unitPrice: 0, vatRateId: "", customValues: {} },
  ]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [showDueDateWarning, setShowDueDateWarning] = useState(false);
  const [visibleRows, setVisibleRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [emailTarget, setEmailTarget] = useState<any | null>(null);
  const [layoutTemplates, setLayoutTemplates] = useState<any[]>([]);
  const [layoutTemplateId, setLayoutTemplateId] = useState<string>("");
  const [globalTemplate, setGlobalTemplate] = useState<any | null>(null);
  // Multi-valuta: valuta + wisselkoers (EUR per 1 eenheid currency).
  const [currency, setCurrency] = useState("EUR");
  const [exchangeRate, setExchangeRate] = useState<string>("1");
  const [rateLoading, setRateLoading] = useState(false);

  /** Haal de EUR-koers op voor een valuta op een datum (frankfurter). */
  const fetchRate = async (cur: string, rateDate: string) => {
    if (!cur || cur.toUpperCase() === "EUR") { setExchangeRate("1"); return; }
    setRateLoading(true);
    try {
      const { data } = await api.get("/api/exchange-rate", { params: { from: cur, date: rateDate } });
      if (typeof data?.rate === "number" && data.rate > 0) setExchangeRate(String(data.rate));
    } catch { /* laat handmatige waarde staan */ }
    finally { setRateLoading(false); }
  };
  const onCurrencyChange = (cur: string) => { setCurrency(cur); fetchRate(cur, date); };

  useEffect(() => {
    loadInvoices();
    loadContacts();
    api.get("/api/vat-rates").then((res) => {
      setVatRates(res.data);
      const defaultVat = res.data.find((v: VatRate) => v.percentage === 21)?.id || res.data[0]?.id;
      setLines([{ description: "", quantity: 1, unitPrice: 0, vatRateId: defaultVat, customValues: {} }]);
    });
    // Beschikbare lay-outtemplates voor de per-document keuze (+ platform-template).
    api.get("/api/layout-templates").then((res) => {
      setLayoutTemplates(res.data.templates || []);
      setGlobalTemplate(res.data.globalTemplate || null);
    });
  }, []);

  const loadContacts = () =>
    api.get("/api/contacts").then((res) => setContacts(res.data));

  const loadInvoices = () => api.get("/api/invoices").then((res) => setInvoices(res.data));

  const addColumn = () => {
    const id = crypto.randomUUID();
    const name = `Kolom ${customColumns.length + 1}`;
    setCustomColumns([...customColumns, { id, name }]);
    setLines((prev) => prev.map((line) => ({ ...line, customValues: { ...line.customValues, [id]: "" } })));
  };

  const updateColumn = (id: string, name: string) => {
    setCustomColumns(customColumns.map((c) => (c.id === id ? { ...c, name } : c)));
  };

  const removeColumn = (id: string) => {
    setCustomColumns(customColumns.filter((c) => c.id !== id));
    setLines((prev) =>
      prev.map((line) => {
        const { [id]: _, ...rest } = line.customValues;
        return { ...line, customValues: rest };
      })
    );
  };

  const addLine = () => {
    const defaultVat = vatRates.find((v) => v.percentage === 21)?.id || vatRates[0]?.id;
    const values: Record<string, string> = {};
    customColumns.forEach((c) => (values[c.id] = ""));
    setLines([...lines, { description: "", quantity: 1, unitPrice: 0, vatRateId: defaultVat, customValues: values }]);
  };

  const removeLine = (i: number) => {
    setLines(lines.filter((_, idx) => idx !== i));
  };

  const updateLine = (i: number, key: keyof Line, value: any) => {
    const next = [...lines];
    next[i] = { ...next[i], [key]: value };
    setLines(next);
  };

  const updateCustomValue = (lineIdx: number, columnId: string, value: string) => {
    const next = [...lines];
    next[lineIdx] = { ...next[lineIdx], customValues: { ...next[lineIdx].customValues, [columnId]: value } };
    setLines(next);
  };

  const totals = useMemo(() => {
    let subtotal = 0;
    let vat = 0;
    const byRate = new Map<number, { base: number; vat: number }>();
    lines.forEach((line) => {
      const rate = vatRates.find((v) => v.id === line.vatRateId)?.percentage ?? 0;
      const lineTotal = (line.quantity || 0) * (line.unitPrice || 0);
      subtotal += lineTotal;
      const vatPart = lineTotal * (rate / 100);
      vat += vatPart;
      const entry = byRate.get(rate) || { base: 0, vat: 0 };
      entry.base += lineTotal;
      entry.vat += vatPart;
      byRate.set(rate, entry);
    });
    const breakdown = [...byRate.entries()]
      .sort((a, b) => b[0] - a[0])
      .map(([percentage, v]) => ({ percentage, ...v }));
    return { subtotal, vat, total: subtotal + vat, breakdown };
  }, [lines, vatRates]);

  const handleSave = async () => {
    setSaveError("");
    if (!contactId) {
      setSaveError("Kies eerst een contact, of maak via ＋ Nieuwe klant een aan.");
      return;
    }
    const noDueDate = !dueDate;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (!l.description.trim()) { setSaveError(`Regel ${i + 1}: vul een omschrijving in.`); return; }
      if (!(Number(l.quantity) > 0)) { setSaveError(`Regel ${i + 1}: aantal moet groter dan 0 zijn.`); return; }
      if (!l.vatRateId) { setSaveError(`Regel ${i + 1}: kies een BTW-tarief.`); return; }
    }
    setSaving(true);
    try {
      const payload = {
        contactId,
        issueDate: date,
        dueDate,
        customColumns,
        layoutTemplateId: layoutTemplateId || null,
        currency: currency || "EUR",
        ...(Number(exchangeRate) > 0 ? { exchangeRate: Number(exchangeRate) } : {}),
        lines: lines.map((l) => ({
          description: l.description,
          quantity: Number(l.quantity),
          unitPrice: Number(l.unitPrice),
          vatRateId: l.vatRateId,
          customValues: l.customValues,
        })),
      };
      if (editing) {
        await api.put(`/api/invoices/${editing.id}`, payload);
      } else {
        await api.post("/api/invoices", payload);
      }
      setIsOpen(false);
      reset();
      loadInvoices();
      if (noDueDate) setShowDueDateWarning(true);
    } catch (err: any) {
      setSaveError(err?.response?.data?.error || "Opslaan mislukt. Controleer de velden en probeer opnieuw.");
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setContactId("");
    setDate(new Date().toISOString().split("T")[0]);
    setDueDate("");
    setCustomColumns([]);
    const defaultVat = vatRates.find((v) => v.percentage === 21)?.id || vatRates[0]?.id;
    setLines([{ description: "", quantity: 1, unitPrice: 0, vatRateId: defaultVat, customValues: {} }]);
    setSaveError("");
    setEditing(null);
    setLayoutTemplateId("");
    setCurrency("EUR");
    setExchangeRate("1");
    setRateLoading(false);
  };

  const openEdit = (inv: any) => {
    const cols: CustomColumn[] = Array.isArray(inv.customColumns) ? inv.customColumns : [];
    setEditing(inv);
    setContactId(inv.contactId);
    setDate(inv.issueDate ? new Date(inv.issueDate).toISOString().split("T")[0] : new Date().toISOString().split("T")[0]);
    setDueDate(inv.dueDate ? new Date(inv.dueDate).toISOString().split("T")[0] : "");
    setCustomColumns(cols);
    setLines(
      (inv.lines || []).map((l: any) => ({
        description: l.description || "",
        quantity: Number(l.quantity) || 1,
        unitPrice: Number(l.unitPrice) || 0,
        vatRateId: l.vatRateId || l.vatRate?.id || "",
        customValues: { ...(l.customValues || {}) },
      }))
    );
    setLayoutTemplateId(inv.layoutTemplateId || "");
    setCurrency(inv.currency || "EUR");
    setExchangeRate(inv.exchangeRate != null ? String(inv.exchangeRate) : "1");
    setSaveError("");
    setIsOpen(true);
  };

  const handleMarkPaid = async (id: string) => {
    await api.post(`/api/invoices/${id}/mark-paid`);
    loadInvoices();
  };

  const duplicate = async (id: string) => {
    await api.post(`/api/invoices/${id}/duplicate`);
    loadInvoices();
  };

  const handleCredit = async (inv: any) => {
    const reason = window.prompt(
      `Factuur ${inv.number} crediteren?\n\nDit maakt een creditfactuur (negatieve tegenfactuur) aan die de oorspronkelijke factuur corrigeert. Geef optioneel een reden op:`,
      ""
    );
    // cancel → null; lege reden is toegestaan.
    if (reason === null) return;
    try {
      await api.post(`/api/invoices/${inv.id}/credit`, { reason: reason.trim() || undefined });
      loadInvoices();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Crediteren mislukt.");
    }
  };

  const handleUndoCredit = async (inv: any) => {
    if (
      !confirm(
        `Creditfactuur ${inv.number} ongedaan maken?\n\nDe creditfactuur wordt verwijderd, zodat de oorspronkelijke factuur weer volledig openstaat.`
      )
    )
      return;
    try {
      await api.post(`/api/invoices/${inv.id}/undo-credit`);
      loadInvoices();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Creditering ongedaan maken mislukt.");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Weet je zeker dat je deze factuur wilt verwijderen? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      await api.delete(`/api/invoices/${id}`);
      loadInvoices();
    } catch (err: any) {
      alert(err?.response?.data?.error || "Verwijderen mislukt.");
    }
  };

  const handleViewPdf = async (inv: any) => {
    try {
      const res = await api.get(`/api/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("PDF laden mislukt.");
    }
  };

  const handleDownloadPdf = async (inv: any) => {
    try {
      const res = await api.get(`/api/invoices/${inv.id}/pdf`, { responseType: "blob", params: { download: "1" } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${inv.number || "factuur"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert("PDF downloaden mislukt.");
    }
  };

  const filtered = invoices.filter((inv) => !statusFilter || inv.status === statusFilter);

  const vatTypes = (inv: any): string => {
    const set = new Set<number>();
    (inv.lines || []).forEach((l: any) => {
      if (l.vatRate?.percentage !== undefined && l.vatRate?.percentage !== null) {
        set.add(Number(l.vatRate.percentage));
      }
    });
    if (!set.size) return "—";
    return [...set].map((p) => `${p}%`).join(", ");
  };

  const columns: Column<any>[] = [
    { key: "number", header: "Nummer", accessor: (inv) => inv.number || "—",
      render: (inv) => (
        <div className="flex flex-col">
          <span className="font-semibold text-slate-800">{inv.number || "—"}</span>
          {inv.isCredit && inv.originalInvoice && (
            <span className="text-[11px] text-rose-600" title={`Creditfactuur t.o.v. ${inv.originalInvoice.number}`}>
              credit t.o.v. {inv.originalInvoice.number}
            </span>
          )}
        </div>
      ) },
    { key: "date", header: "Datum", accessor: (inv) => inv.issueDate ? fmtDate(inv.issueDate) : "—",
      sortValue: (inv) => new Date(inv.issueDate).getTime() },
    { key: "time", header: "Tijd", accessor: (inv) => inv.createdAt ? fmtTime(inv.createdAt) : "—",
      sortValue: (inv) => new Date(inv.createdAt).getTime(), sortable: false },
    { key: "party", header: "Klant", accessor: (inv) => inv.contact?.companyName || inv.contact?.attentionTo || inv.contact?.name || "—" },
    { key: "gross", header: "Bruto",
      accessor: (inv) => eur(inv.totalEur ?? inv.total), className: "text-right",
      sortValue: (inv) => Number(inv.totalEur ?? inv.total),
      render: (inv) => (
        <div className="text-right leading-tight">
          <div>{fmtCurrency(inv.total, inv.currency)}</div>
          {inv.currency && inv.currency !== "EUR" && inv.totalEur != null && (
            <div className="text-xs text-slate-400">≈ {eur(inv.totalEur)}</div>
          )}
        </div>
      ) },
    { key: "vat", header: "BTW",
      accessor: (inv) => eur(inv.vatTotalEur ?? inv.vatTotal), className: "text-right",
      sortValue: (inv) => Number(inv.vatTotalEur ?? inv.vatTotal),
      render: (inv) => (
        <div className="text-right leading-tight">
          <div>{fmtCurrency(inv.vatTotal, inv.currency)}</div>
          {inv.currency && inv.currency !== "EUR" && inv.vatTotalEur != null && (
            <div className="text-xs text-slate-400">≈ {eur(inv.vatTotalEur)}</div>
          )}
        </div>
      ) },
    { key: "net", header: "Netto",
      accessor: (inv) => eur(inv.subtotalEur ?? inv.subtotal), className: "text-right",
      sortValue: (inv) => Number(inv.subtotalEur ?? inv.subtotal),
      render: (inv) => (
        <div className="text-right leading-tight">
          <div>{fmtCurrency(inv.subtotal, inv.currency)}</div>
          {inv.currency && inv.currency !== "EUR" && inv.subtotalEur != null && (
            <div className="text-xs text-slate-400">≈ {eur(inv.subtotalEur)}</div>
          )}
        </div>
      ) },
    { key: "vatType", header: "BTW-soort", accessor: (inv) => vatTypes(inv) },
    { key: "due", header: "Vervaldatum", accessor: (inv) => inv.dueDate ? fmtDate(inv.dueDate) : "—",
      sortValue: (inv) => inv.dueDate ? new Date(inv.dueDate).getTime() : 0 },
    { key: "status", header: "Status", accessor: (inv) => nlStatus(inv.status),
      render: (inv) => <Badge status={inv.status} pulse={inv.status === "OVERDUE"}>{nlStatus(inv.status)}</Badge> },
    { key: "actions", header: "Acties", accessor: () => "", sortable: false, width: "1%",
      render: (inv) => (
        <RowActions
          items={[
            { label: "PDF openen", icon: FileText, onClick: () => handleViewPdf(inv) },
            { label: "Download PDF", icon: Download, onClick: () => handleDownloadPdf(inv) },
            { label: "Export PDF", icon: Printer, onClick: () => setPreviewId(inv.id) },
            { label: "Export CSV", icon: FileDown, onClick: () => exportRowsToCSV([inv], columns, inv.number || "factuur") },
            { label: "Dupliceren", icon: Copy, onClick: () => duplicate(inv.id) },
            { label: "Versturen", icon: Mail, onClick: () => setEmailTarget(inv), hidden: inv.status !== "DRAFT" },
            { label: "Bewerken", icon: Pencil, onClick: () => openEdit(inv) },
            { label: "Markeer als betaald", icon: CheckCircle, onClick: () => handleMarkPaid(inv.id), hidden: !(inv.status === "SENT" || inv.status === "OVERDUE") },
            { label: "Crediteren", icon: FileMinus2, onClick: () => handleCredit(inv), hidden: inv.isCredit || !["SENT", "PAID", "OVERDUE"].includes(inv.status) },
            { label: "Crediteren ongedaan maken", icon: Undo2, onClick: () => handleUndoCredit(inv), hidden: !inv.isCredit },
          ]}
          onDelete={() => handleDelete(inv.id)}
          deleteHidden={!isSuperadmin}
        />
      ) },
  ];

  const statusOptions = [
    { value: "", label: "Alle statussen" },
    { value: "DRAFT", label: "Concept" },
    { value: "SENT", label: "Verstuurd" },
    { value: "PAID", label: "Betaald" },
    { value: "OVERDUE", label: "Achterstallig" },
    { value: "CREDIT", label: "Creditfacturen" },
  ];

  const openModal = () => {
    reset();
    setIsOpen(true);
  };

  const exportRows = selected.length ? selected : visibleRows;

  return (
    <div className="space-y-6 animate-fade-in-up">
      <PageHeader
        title="Facturen"
        subtitle="Beheer je verkoopfacturen"
        action={
          <Button onClick={openModal}>
            <Plus size={27} /> Nieuwe factuur
          </Button>
        }
      />

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(inv) => inv.id}
        searchPlaceholder="Zoek facturen..."
        storageKey="invoices"
        initialSort={{ key: "date", dir: "desc" }}
        groupBy={{ getDate: (inv) => inv.issueDate, getAmount: (inv) => Number(inv.totalEur ?? inv.total) }}
        emptyMessage="Geen facturen gevonden."
        onVisibleRowsChange={setVisibleRows}
        selectable
        onSelectionChange={setSelected}
        toolbar={
          <>
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-48"
            />
            {selected.length > 0 && (
              <span className="text-xs font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-lg px-2.5 py-1.5">
                {selected.length} geselecteerd
              </span>
            )}
            <TableExportToolbar
              rows={exportRows}
              columns={columns}
              filename={selected.length ? `facturen-selectie` : "facturen"}
              source="facturen"
              previewKind="invoices"
            />
          </>
        }
      />

      <PrintTable rows={exportRows} columns={columns} title="Facturen" />

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={editing ? "Factuur bewerken" : "Nieuwe factuur"} subtitle={editing ? `Pas factuur ${editing.number} aan` : "Vul contact, datums, kolommen en regels in"} size="3xl">
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="Contact"
              options={[
                { value: "", label: "Kies een contact..." },
                { value: "__new__", label: "＋ Nieuwe klant aanmaken..." },
                ...contacts.map((c) => ({ value: c.id, label: c.name })),
              ]}
              value={contactId}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setContactId("");
                  setShowContactModal(true);
                } else {
                  setContactId(e.target.value);
                }
              }}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input label="Factuurdatum" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              <Input label="Vervaldatum" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            </div>
            <Select
              label="Lay-out"
              options={[
                { value: "", label: "Standaard (automatisch)" },
                ...(globalTemplate
                  ? [{ value: globalTemplate.id, label: `${globalTemplate.name} (platform)` }]
                  : []),
                ...layoutTemplates
                  .filter((t) => t.id !== globalTemplate?.id)
                  .map((t) => ({
                    value: t.id,
                    label: `${t.name}${t.isDefault ? " ★" : ""}`,
                  })),
              ]}
              value={layoutTemplateId}
              onChange={(e) => setLayoutTemplateId(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <CurrencySelect label="Valuta" value={currency} onChange={(e) => onCurrencyChange(e.target.value)} />
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Wisselkoers (EUR per 1 {currency})
              </label>
              <div className="flex items-center gap-2">
                <Input
                  value={exchangeRate}
                  onChange={(e) => setExchangeRate(e.target.value)}
                  disabled={currency === "EUR"}
                />
                {rateLoading && <span className="text-xs text-slate-400">…</span>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-bold text-slate-600 flex items-center gap-1">
              <Columns3 size={24} /> Extra kolommen:
            </span>
            {customColumns.map((col) => (
              <EditableChip
                key={col.id}
                value={col.name}
                onChange={(name) => updateColumn(col.id, name)}
                onRemove={() => removeColumn(col.id)}
              />
            ))}
            <Button variant="outline" size="sm" onClick={addColumn}>
              <Plus size={21} /> Kolom toevoegen
            </Button>
          </div>

          <div className="rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/80 border-b border-slate-100">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 min-w-[160px]">Omschrijving</th>
                  {customColumns.map((col) => (
                    <th key={col.id} className="px-4 py-3 text-left font-semibold text-slate-600 min-w-[100px]">{col.name}</th>
                  ))}
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 w-24">Aantal</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 w-28">Prijs</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-600 w-36">BTW</th>
                  <th className="px-4 py-3 text-right font-semibold text-slate-600 w-28">Totaal</th>
                  <th className="px-2 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-4 py-2">
                      <input
                        className="w-full rounded-xl border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        value={line.description}
                        onChange={(e) => updateLine(i, "description", e.target.value)}
                        placeholder="Omschrijving"
                      />
                    </td>
                    {customColumns.map((col) => (
                      <td key={col.id} className="px-4 py-2">
                        <input
                          className="w-full rounded-xl border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                          value={line.customValues[col.id] || ""}
                          onChange={(e) => updateCustomValue(i, col.id, e.target.value)}
                          placeholder={col.name}
                        />
                      </td>
                    ))}
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        className="w-full rounded-xl border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        value={line.quantity}
                        onChange={(e) => updateLine(i, "quantity", Number(e.target.value))}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        step="0.01"
                        className="w-full rounded-xl border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        value={line.unitPrice}
                        onChange={(e) => updateLine(i, "unitPrice", Number(e.target.value))}
                      />
                    </td>
                    <td className="px-4 py-2">
                      <select
                        className="w-full rounded-xl border border-slate-200 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500"
                        value={line.vatRateId}
                        onChange={(e) => updateLine(i, "vatRateId", e.target.value)}
                      >
                        {vatRates.map((v) => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-right font-bold">
                      {fmtCurrency((line.quantity || 0) * (line.unitPrice || 0), currency)}
                    </td>
                    <td className="px-2 py-2">
                      {lines.length > 1 && (
                        <button
                          onClick={() => removeLine(i)}
                          className="p-1.5 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500 transition-colors"
                        >
                          <Trash2 size={24} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Button variant="outline" onClick={addLine}>+ Regel toevoegen</Button>

          <div className="flex justify-end">
            <div className="text-right space-y-1 p-4 rounded-2xl bg-slate-50/80 border border-slate-100 min-w-[220px]">
              <p className="text-sm text-slate-500">Subtotaal: <span className="font-medium text-slate-800">{fmtCurrency(totals.subtotal, currency)}</span></p>
              {totals.breakdown.map((b) => (
                <p key={b.percentage} className="text-sm text-slate-500">BTW {b.percentage}%: <span className="font-medium text-slate-800">{fmtCurrency(b.vat, currency)}</span></p>
              ))}
              <p className="text-lg font-bold text-slate-900">Totaal: {fmtCurrency(totals.total, currency)}</p>
              {currency !== "EUR" && Number(exchangeRate) > 0 && (
                <p className="text-xs text-slate-400">≈ {eur(totals.total * Number(exchangeRate))} in EUR</p>
              )}
            </div>
          </div>

          {saveError && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{saveError}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={saving}>Annuleren</Button>
            <Button onClick={handleSave} loading={saving}>{editing ? "Wijzigingen opslaan" : "Factuur opslaan"}</Button>
          </div>
        </div>
      </Modal>

      <ContactFormModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
        onSaved={(c) => {
          setShowContactModal(false);
          loadContacts().then(() => setContactId(c.id));
        }}
      />

      <PreviewModal
        isOpen={!!previewId}
        onClose={() => setPreviewId(null)}
        kind="invoices"
        id={previewId}
        title="Factuur weergave"
      />

      <EmailModal
        isOpen={!!emailTarget}
        onClose={() => setEmailTarget(null)}
        kind="invoices"
        docId={emailTarget?.id || ""}
        docNumber={emailTarget?.number || ""}
        contactName={emailTarget?.contact?.companyName || emailTarget?.contact?.name}
        contactEmail={emailTarget?.contact?.email}
        companyName={activeCompany?.name}
        onSent={loadInvoices}
      />

      <AlertModal
        isOpen={showDueDateWarning}
        onClose={() => setShowDueDateWarning(false)}
        title="Let op"
        message="De 'Vervaldatum' is niet opgevoerd. De factuur is zonder vervaldatum opgeslagen."
      />
    </div>
  );
}
